import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import { HubInstance, instanceApi } from '../api';
import { useI18n } from '../i18n';
import { DataTable, DataTableColumn } from '../table';

function seenLabel(t: (key: string, vars?: { n: number }) => string, seconds: number | null): string {
  if (seconds == null) return t('instances.never');
  if (seconds < 90) return t('instances.secondsAgo', { n: seconds });
  if (seconds < 5400) return t('instances.minutesAgo', { n: Math.round(seconds / 60) });
  if (seconds < 172800) return t('instances.hoursAgo', { n: Math.round(seconds / 3600) });
  return t('instances.daysAgo', { n: Math.round(seconds / 86400) });
}

function errorText(err: unknown, fallback: string): string {
  const response = (err as { response?: { data?: { error?: string; detail?: string } } })?.response;
  const message = [response?.data?.error, response?.data?.detail].filter(Boolean).join(' — ');
  return message || fallback;
}

const InstancesPage: React.FC = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState<HubInstance[]>([]);
  const [name, setName] = useState('');
  const [customer, setCustomer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const instances = await instanceApi.list();
      setRows(instances);
      setUnavailable(false);
      setError(null);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setUnavailable(true);
        setRows([]);
        return;
      }
      setError(errorText(err, t('instances.loadError')));
    }
  }, [t]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 10000);
    return () => window.clearInterval(timer);
  }, [load]);

  const columns = useMemo<DataTableColumn<HubInstance>[]>(() => [
    {
      id: 'url',
      label: t('instances.url'),
      accessor: (row) => row.url || row.name,
      render: (row) => row.url ? (
        <a href={row.url} target="_blank" rel="noreferrer">{row.url}</a>
      ) : row.name,
    },
    {
      id: 'version',
      label: t('instances.version'),
      accessor: (row) => row.version || '—',
    },
    {
      id: 'devices',
      label: t('instances.devices'),
      align: 'right',
      accessor: (row) => t('instances.devicesValue', { online: row.devicesOnline, total: row.devices }),
      sortValue: (row) => row.devices,
    },
    {
      id: 'status',
      label: t('instances.status'),
      filter: 'select',
      accessor: (row) => (row.active ? t('instances.active') : t('instances.quiet')),
    },
    {
      id: 'seen',
      label: t('instances.seen'),
      accessor: (row) => seenLabel(t, row.seenAgoSec),
      sortValue: (row) => (row.seenAgoSec == null ? Number.MAX_SAFE_INTEGER : row.seenAgoSec),
    },
    {
      id: 'actions',
      label: '',
      filter: 'none',
      sortable: false,
      stopRowClick: true,
      accessor: () => '',
      render: (row) => (row.managed ? (
        <Button
          size="small"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await instanceApi.update(row.id);
              await load();
            } catch (err) {
              setError(errorText(err, t('instances.updateError', { id: row.id })));
            } finally {
              setBusy(false);
            }
          }}
        >
          {t('instances.update')}
        </Button>
      ) : null),
    },
  ], [busy, load, t]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const out = await instanceApi.create({
        name: name.trim() || undefined,
        customer: customer.trim() || undefined,
      });
      setNotice([
        t('instances.created', { url: out.instance.url }),
        t('instances.passwordOnce', {
          user: out.secrets.adminUser,
          password: out.secrets.adminPassword,
        }),
      ].join(' '));
      setName('');
      setCustomer('');
      await load();
    } catch (err) {
      setError(errorText(err, t('instances.createError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container sx={{ py: 3 }}>
      <Typography variant="h5">{t('instances.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('instances.subtitle')}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      {unavailable ? (
        <Typography color="text.secondary">{t('instances.unavailable')}</Typography>
      ) : (
        <>
          <DataTable
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            emptyMessage={t('instances.empty')}
          />
          <Box
            component="form"
            onSubmit={create}
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end', mt: 3 }}
          >
            <TextField
              label={t('instances.name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
            />
            <TextField
              label={t('instances.customer')}
              value={customer}
              onChange={(event) => setCustomer(event.target.value)}
              size="small"
            />
            <Button type="submit" variant="contained" disabled={busy}>
              {t('instances.create')}
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
};

export default InstancesPage;
